import { RootState, ThunkDispatch } from "core-integration/src/store";
import {
  getActiveChip,
  getActiveOrProposedTeamChipName,
  teamChipsHaveChanged,
} from "core-integration/src/store/chips/reducers";
import { getElementsById } from "core-integration/src/store/elements/reducers";
import { fetchEventLive } from "core-integration/src/store/elements/thunks";
import { getEntry } from "core-integration/src/store/entries/reducers";
import {
  fetchEntrySummary,
  submitEntryImage,
} from "core-integration/src/store/entries/thunks";
import { IPickLight } from "core-integration/src/store/entries/types";
import {
  getCurrentEvent,
  getNextEvent,
} from "core-integration/src/store/events/reducers";
import { fetchFixtures } from "core-integration/src/store/fixtures/thunks";
import { getRules } from "core-integration/src/store/game/reducers";
import {
  getMyFormation,
  getMyPicksProposed,
  getMyTeamSavingState,
  hasMyTeamChanged,
  isMyTeamValid,
} from "core-integration/src/store/my-team/reducers";
import {
  fetchMyTeam,
  saveMyTeam,
} from "core-integration/src/store/my-team/thunks";
import { IPickProposed } from "core-integration/src/store/my-team/types";
import { getPlayerData } from "core-integration/src/store/player/reducers";
import { ILoggedInPlayer } from "core-integration/src/store/player/types";
import { getRegionsById } from "core-integration/src/store/regions/reducers";
import type { EntryCrestData } from "core-integration/src/store/ui";
import { openCrestSheet } from "core-integration/src/store/ui";
import { range } from "lodash";
import { Alert } from "plos/src/components/alerts";
import { Button } from "plos/src/components/buttons/Button";
import DeadlineBar from "plos/src/components/DeadlineBar";
import { Fixtures } from "plos/src/components/Fixtures";
import PageTitle from "plos/src/components/PageTitle";
import Pitch from "plos/src/components/Pitch";
import { SurfaceContainer } from "plos/src/components/SurfaceContainer";
import { ElementIconLink } from "plos/src/components/tooltips/ElementIconLink";
import { useLocalizedNavigate } from "plos/src/hooks/useLocalizedNavigate";
import { leftSidebarLayout, squadMain } from "plos/src/layouts";
import { actionBar, edgeToEdge } from "plos/src/styles/utils.css";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { useLocation } from "react-router";
import { useAdobeContext } from "../../contexts/AdobeContext";
import {
  useOnboardingContext,
  type LocationState,
} from "../../contexts/OnboardingContext";
import { OverlayProvider } from "../../contexts/OverlayContext";
import { useTrackingContext } from "../../contexts/TrackingContext";
import { usePitchView } from "../../hooks/usePitchView";
import DreamTeam from "../../img/icons/dreamteam.svg?react";
import { getChipName } from "../../utils/chips";
import ChipList from "../Chips/ChipList";
import { ClubTeamNews } from "../ClubTeamNews";
import { EntryCrest } from "../crests/EntryCrest";
import InitialCrestSheet from "../crestSheets/InitialCrestSheet";
import EntryInfo from "../EntryInfo";
import { HelmetHead } from "../HelmetHead";
import LatestAlert from "../LatestAlert";
import { LeaderboardAd } from "../LeaderboardAd";
import { PitchControls } from "../PitchControls";
import PlayerInfoPanel from "../PlayerInfoPanel";
import { PlayerPreviewSheet } from "../PlayerSheets/PlayerPreviewSheet";
import { dreamTeamIcon } from "../TeamPitchElement/teamPitchElement.css";
import { ContextualHelpSheet } from "../Tutorials";
import PickTeamHelp from "../Tutorials/HelpPages/PickTeamHelp";
import { Bench, BenchUnit } from "./Bench";
import { playerInfoWrap, teamPitchWrap } from "./myTeam.css";
import { MyTeamPlayerActions } from "./MyTeamPlayerActions";
import MyTeamTable from "./MyTeamTable";
import PitchFormation from "./PitchFormation";
import { TeamSavedModal, useTeamSavedModal } from "./TeamSavedModal";
import { WhatsNewSheet } from "./WhatsNewSheet";
import { WhatsNextSheet } from "./WhatsNextSheet";

const MyTeam = () => {
  const { t } = useTranslation("pickTeam");

  const { fireClickTrackEvent, firePageViewEvent } = useTrackingContext();
  const { activeView, handleViewChange } = usePitchView();
  const [pickForMenu, setPickForMenu] = useState<IPickProposed | null>(null);
  const [isWhatsNewSheetOpen, setWhatsNewSheetOpen] = useState(false);
  const [isInitialCrestSheetOpen, setInitialCrestSheetOpen] = useState(false);
  const { enabled: adobeEnabled } = useAdobeContext();
  const { autopickChoice } = useOnboardingContext();

  const player = useSelector(getPlayerData) as ILoggedInPlayer; // enforced by EntryRoute
  const activeChip = useSelector(getActiveChip);
  const chipInPlayName = useSelector(getActiveOrProposedTeamChipName);
  const changed = useSelector(hasMyTeamChanged);
  const chipsChanged = useSelector(teamChipsHaveChanged);
  const currentEvent = useSelector(getCurrentEvent);
  const elementsById = useSelector((state: RootState) =>
    getElementsById(state, undefined, activeChip?.id),
  );
  const entry = useSelector((state: RootState) =>
    getEntry(state, player.entry),
  );
  const regionsById = useSelector(getRegionsById);
  const region = player.region ? regionsById[player.region] : undefined;
  const formation = useSelector((state: RootState) =>
    getMyFormation(state, undefined, activeChip?.id),
  );
  const nextEvent = useSelector(getNextEvent);

  const picks = useSelector(getMyPicksProposed);
  const rules = useSelector((state: RootState) =>
    getRules(state, undefined, activeChip?.id),
  );
  const savingState = useSelector(getMyTeamSavingState);
  const valid = useSelector(isMyTeamValid);

  const dispatch = useDispatch<ThunkDispatch>();
  const navigate = useLocalizedNavigate();
  const location = useLocation();
  const state = location.state as LocationState | null;
  // isNewEntry is true when the user is navigated here straight after creating an entry in squad selection
  const isNewEntry = state?.isNewEntry === true;
  const { showTeamSavedModal, closeTeamSavedModal, teamSavedPromotion } =
    useTeamSavedModal();
  const save = () => {
    dispatch(saveMyTeam());
    fireClickTrackEvent(
      {
        event_category: "fantasy team",
        event_component: "fantasy classic clicks",
        event_type: "save team",
      },
      "fantasy pick team",
      activeView === "pitch" ? "pitch view" : "list view",
    );
  };

  useEffect(() => {
    firePageViewEvent(
      "fantasy pick team",
      activeView === "pitch" ? "pitch view" : "list view",
    );
  }, []);

  useEffect(() => {
    // No more changes to be made, shouldn't be here ...
    if (!nextEvent) {
      setTimeout(() => navigate("/", { replace: true }), 0);
    }
    dispatch(fetchMyTeam());
    dispatch(fetchEntrySummary(player.entry));
    if (currentEvent) {
      dispatch(fetchFixtures(currentEvent.id));
      dispatch(fetchEventLive(currentEvent.id));
    }
  }, [currentEvent, dispatch, navigate, nextEvent, player.entry]);

  // After squad selection, check entry for existing badge
  // If they have one, open the crest sheet and offer to keep or edit, otherwise open the initial crest sheet
  useEffect(() => {
    if (!isNewEntry) return;

    if (!adobeEnabled) {
      setWhatsNewSheetOpen(true);
      // Clear isNewEntry from history so a refresh does not open the What's New sheets again
      navigate("/my-team", { replace: true, state: null });
      return;
    }

    if (!entry) return;

    if (entry.club_badge_src !== null) {
      dispatch(
        openCrestSheet({
          id: entry.id,
          name: entry.name,
          club_badge_src: entry.club_badge_src,
          isNewEntry: true,
          onCancel: () => setWhatsNewSheetOpen(true),
        }),
      );
    } else {
      setInitialCrestSheetOpen(true);
    }
    // Clear isNewEntry from history so a refresh does not open the crest sheets again
    navigate("/my-team", { replace: true, state: null });
  }, [adobeEnabled, dispatch, entry, isNewEntry, navigate]);

  // Show whats new / what's next sheet after closing initial crest sheet
  const handleOpenNextSheet = () => {
    setInitialCrestSheetOpen(false);
    setWhatsNewSheetOpen(true);
  };

  const handleCreateCrest = async (img: Blob, prompt: string) => {
    await dispatch(submitEntryImage(entry.id, img, prompt));
  };

  const renderDreamTeam = (pick: IPickLight) =>
    elementsById[pick.element].in_dreamteam ? (
      <ElementIconLink
        to="/team-of-the-week/"
        label={t("dreamTeamLabel", "Dream Team")}
      >
        <DreamTeam className={dreamTeamIcon} aria-hidden />
      </ElementIconLink>
    ) : null;

  const handleSave = () => {
    save();
    showTeamSavedModal();
  };

  const element = pickForMenu ? elementsById[pickForMenu.element] : undefined;

  const handleElementCardClick = (element: number) => {
    const matches = picks.filter((p) => p.element === element);
    if (matches.length) {
      setPickForMenu(matches[0]);
    }
    fireClickTrackEvent(
      {
        event_category: "fantasy player",
        event_component: "fantasy classic clicks",
        event_detail: "click icon",
        event_type: "click player",
        modal_name: "",
        player_id: elementsById[element].code,
        team_id: elementsById[element].team_code,
      },
      "fantasy pick team",
      activeView === "pitch" ? "pitch view" : "list view",
    );
  };

  const handleElementListItemClick = (pick: IPickProposed) => {
    setPickForMenu(pick);
  };
  const handleClosePlayerSheet = () => {
    setPickForMenu(null);
  };

  if (!entry || !picks.length) {
    return null;
  }

  const entryCrestData: EntryCrestData = {
    id: entry.id,
    name: entry.name,
    club_badge_src: entry.club_badge_src,
  };

  const getBenchPickIndexes = () =>
    range(rules.squad_squadplay, rules.squad_squadsize);

  const canSave =
    (changed || chipsChanged) && valid && savingState !== "saving";

  return (
    <OverlayProvider defaultOverlay="opponent" page="myTeam">
      <HelmetHead
        title={t(
          "metadata.title",
          "Pick Your Fantasy Football Team | Fantasy Premier League",
        )}
        description={t(
          "metadata.description",
          "To pick your Fantasy Premier League team and consider using one of your chips before the next Gameweek deadline, visit the official website of the Premier League.",
        )}
      />
      <LeaderboardAd slot="Landscape_Top" id="ism-pick-ad" />
      <ContextualHelpSheet
        title={t("contextualHelp.pickTeam.title", {
          ns: "help",
          defaultValue: "How to use the Pick Team page",
        })}
      >
        <PickTeamHelp />
      </ContextualHelpSheet>
      <div className={leftSidebarLayout}>
        <div className={squadMain}>
          <section>
            <SurfaceContainer>
              <div className={teamPitchWrap}>
                <LatestAlert />
                <PageTitle title={t("pickTeam", "Pick Team")} />
                {nextEvent && (
                  <>
                    <DeadlineBar />
                  </>
                )}
                <div className={playerInfoWrap}>
                  <EntryCrest entryCrestData={entryCrestData} dimension={35} />
                  <PlayerInfoPanel
                    entry={entry}
                    player={player}
                    region={region}
                  />
                </div>
                <ChipList chipsShown="all" />
                <Alert isInline={true} isCentered>
                  {t(
                    "howChangeCaptain",
                    "To change your captain use the menu which appears when clicking on a player",
                  )}
                </Alert>

                <PitchControls
                  activeView={activeView}
                  handleViewChange={handleViewChange}
                />

                {activeView === "pitch" && (
                  <div className={edgeToEdge}>
                    <Pitch sponsor="default">
                      <PitchFormation
                        chipName={chipInPlayName}
                        eventId={nextEvent?.id}
                        formation={formation}
                        picks={picks}
                        renderDreamTeam={renderDreamTeam}
                        renderElementMenu={handleElementCardClick}
                      />

                      <Bench variant={activeChip?.name}>
                        {getBenchPickIndexes().map(
                          (e, i) =>
                            picks[e] && (
                              <BenchUnit
                                key={e}
                                index={i}
                                pick={picks[e]}
                                eventId={nextEvent?.id}
                                renderElementMenu={() =>
                                  handleElementCardClick(picks[e].element)
                                }
                                renderDreamTeam={renderDreamTeam}
                              />
                            ),
                        )}
                      </Bench>
                    </Pitch>
                  </div>
                )}
                {activeView === "list" && (
                  <MyTeamTable
                    picks={picks}
                    chipInPlayName={chipInPlayName}
                    elementsById={elementsById}
                    handleElementClick={handleElementListItemClick}
                    renderDreamTeam={renderDreamTeam}
                  />
                )}

                {activeChip && activeChip.chip_type === "transfer" && (
                  <Alert isContentCentered>
                    {t("chipActive", "{{chipName}} Active", {
                      chipName: getChipName(t, activeChip.name),
                    })}
                  </Alert>
                )}
                {savingState === "saved" ? (
                  <Alert isContentCentered>
                    {t("teamHasBeenSaved", "Your team has been saved.")}
                  </Alert>
                ) : (
                  canSave && (
                    <div className={actionBar}>
                      <Button
                        onPress={handleSave}
                        fullWidth
                        isDisabled={!canSave}
                      >
                        {t("saveYourTeam", "Save Your Team")}
                      </Button>
                    </div>
                  )
                )}
              </div>
            </SurfaceContainer>
          </section>

          <ClubTeamNews />

          {nextEvent && (
            <section>
              <SurfaceContainer>
                <Fixtures eventId={nextEvent.id} />
              </SurfaceContainer>
            </section>
          )}
        </div>

        <section>
          <SurfaceContainer>
            <EntryInfo entryId={entry.id} />
          </SurfaceContainer>
        </section>
      </div>
      <PlayerPreviewSheet
        element={element}
        open={Boolean(element)}
        handleClose={handleClosePlayerSheet}
        actionButtons={({ handleViewFullProfile }) => {
          if (!pickForMenu) return;
          return (
            <MyTeamPlayerActions
              handleViewFullProfile={handleViewFullProfile}
              pick={pickForMenu}
              handleClosePlayerSheet={handleClosePlayerSheet}
            />
          );
        }}
      />
      <TeamSavedModal
        handleCreateCrest={handleCreateCrest}
        currentPromotion={teamSavedPromotion}
        onClose={closeTeamSavedModal}
      />
      <InitialCrestSheet
        open={isInitialCrestSheetOpen}
        handleClose={() => setInitialCrestSheetOpen(false)}
        handleCreateCrest={handleCreateCrest}
        handleOpenNextSheet={handleOpenNextSheet}
      />
      {/* Whats New shown to users who picked their team manually */}
      <WhatsNewSheet
        isOpen={isWhatsNewSheetOpen && autopickChoice === "manual_pick"}
        onClose={() => setWhatsNewSheetOpen(false)}
      />
      {/* Whats Next shown to users who used the tutorial, with the assumption that they are newer players */}
      <WhatsNextSheet
        isOpen={isWhatsNewSheetOpen && autopickChoice === "show_tutorial"}
        onClose={() => setWhatsNewSheetOpen(false)}
      />
    </OverlayProvider>
  );
};

export default MyTeam;
